import { DateTime, DateTimeRange, Duration, DurationFormat, RequestSlotValues } from "./models";
import { format, isSameDay, parse } from "date-fns";

export enum ListDelimiter {
    or,
    and
}
/**
 * Based on the provided slot value, it will return the appropriate <say-as> tag.
 * 
 * {@link https://cloud.google.com/text-to-speech/docs/ssml#say%E2%80%91as}
 * {@link https://developer.amazon.com/en-US/docs/alexa/custom-skills/speech-synthesis-markup-language-ssml-reference.html#say-as}
 * {@link https://docs.aws.amazon.com/polly/latest/dg/supportedtags.html#say-as-tag}
 * 
 * @param value 
 * @returns
 */
 export function slotValueToSpeech(value: RequestSlotValues, type: "ssml" | "displayText" = "ssml"): string {

    let speech: string;

    switch (typeof value) {
        case "string":
            speech = value;
            break;
        case "number":
            speech = `${value}`;
            break;
        default:
        // let it remain undefined
    }

    if (isDateTime(value)) {
        speech = dateTimeToSpeech(value, type);
    }

    if (isDateTimeRange(value)) {
        speech = dateTimeRangeToSpeech(value, type);
    }

    if (isDuration(value)) {
        speech = durationToSpeech(value, type);
    }

    if (Array.isArray(value)) {
        // We have an array, listisize
        // Not sure how we let them decide if it is an AND or and OR situation
        // Right now we are using AND
        speech = listisize(value, ListDelimiter.and);
    }

    return speech;
}

/**
 * Determine if the request slot value is a DateTime
 *
 * @public
 * @param slotValue - Slot value to check 
 */
 export function isDateTime(slotValue: RequestSlotValues): slotValue is DateTime {
    return !!slotValue && (!!(slotValue as DateTime).date || !!(slotValue as DateTime).time);
}
/**
 * Determine if the request slot value is a DateTimeRange
 *
 * @public
 * @param slotValue - Slot value to check
 */
 export function isDateTimeRange(slotValue: RequestSlotValues): slotValue is DateTimeRange {
    return !!slotValue && (!!(slotValue as DateTimeRange).start || !!(slotValue as DateTimeRange).end);
}
/**
 * Determine if the request slot value is a Duration
 *
 * @public
 * @param slotValue - Slot value to check 
 */
 export function isDuration(slotValue: RequestSlotValues): slotValue is Duration {
    return !!slotValue && typeof slotValue === "object" && typeof (slotValue as Duration).amount === "number" && typeof (slotValue as Duration).format === "string";
}

/**
 * Converts a date time object to a string that can be used in either SSML or display text.
 * 
 * @privateRemarks
 * We will want to consider how we tackle localization of dates.  date-fns
 * can handle it we just need to pass in the locale.  Additionally, when generating
 * a response for display, we may want to support a short and long form.
 * 
 * @beta 
 * @param value 
 * @param type 
 */
 export function dateTimeToSpeech(value: DateTime, type: "ssml" | "displayText" = "ssml"): string {

    if (!value) {
        return "";
    }

    // If it is a time only, we need to just add the 
    // current date for parsing.  It won't be spoken 
    // back to the user, just for parsing.
    const toParse = { ...value };

    if (!toParse.date) {
        toParse.date = (new Date(), "yyyy-MM-dd");
    }
    const date = Date.parse(dateTimeToString(toParse));
    let speech = "";
    if (value.date) {
        if (type === "ssml") {
            speech += `<say-as interpret-as="date" format="ymd">${format(date, "yyyy-MM-dd")}</say-as>`;
        } else {
            speech += `${format(date, "M-d-yyyy")}`
        }
    }

    if (value.time) {
        if (type === "ssml") {
            speech += `<say-as interpret-as="time">${format(date, "h:mm aa")}</say-as>`;
        } else {
            if (speech.length > 0) {
                speech += " ";
            }
            speech += `${format(date, "h:mm aa")}`
        }
    }

    return speech;
}
export function dateTimeRangeToSpeech(value: DateTimeRange, type: "ssml" | "displayText" = "ssml"): string {

    if (!value) {
        return "";
    }

    const start = value.start;
    const end = value.end;

    const refDate = new Date();

    // The default value
    let speech = `${dateTimeToSpeech(start, type)} to ${dateTimeToSpeech(end, type)}`;

    // We want to shorten this down if we can before
    // defaulting to {startDate} to {endDate}, which 
    // can be verbose.  
    //
    // Case 1: If it is the same date, just communicate the time difference
    // "on {date} from {time} to {time}"
    if (isSameDay(parse(start.date, "y-M-d", refDate), parse(end.date, "y-M-d", refDate))) {
        speech = `on ${dateTimeToSpeech({ date: start.date }, type)} from ${dateTimeToSpeech({ time: start.time }, type)} to ${dateTimeToSpeech({ time: end.time }, type)}`
    }

    return speech;
}
export function durationToSpeech(duration: Duration, type: "ssml" | "displayText" = "ssml"): string {

    if (!duration) {
        return "";
    }

    const plural: boolean = duration.amount > 1;
    const amount = type === "ssml" ? `<say-as interpret-as="cardinal">${duration.amount}</say-as>` : duration.amount;

    // We need to get the singular, long form version of the format.
    const multiplier = DURATION_FORMAT_TO_MS_MULTIPLIER[duration.format];

    let rawFormat = duration.format;

    // Now find the one the doesn't end in "s" and the length is greater than 1
    Object.keys(DURATION_FORMAT_TO_MS_MULTIPLIER).forEach((key: DurationFormat) => {
        if (DURATION_FORMAT_TO_MS_MULTIPLIER[key] === multiplier) {
            if (key.length > rawFormat.length && !key.endsWith("s")) {
                rawFormat = key;
            }
        }
    });

    const format = plural ? `${rawFormat}s` : rawFormat;

    return `${amount} ${format}`;
}
/**
 * Converts a date time object to a string.
 *
 * Either a single date (2020-07-19T23:59:59) or a range (2019-07-19T00:00:00 --> 2020-07-19T23:59:59).
 *
 * @param dateTime - Either DateTime or DateTimeRange to convert to a string
 */
export function dateTimeToString(dateTime: DateTime | DateTimeRange): string {

    if (typeof dateTime !== "object") {
        return "";
    }

    let str = "";

    try {
        if (isDateTime(dateTime)) {
            const date = dateTime.date ? format(parse(dateTime.date, "y-M-d", new Date()), "yyyy-MM-dd") : undefined;
            const time = dateTime.time;
            str = `${date ? date : ""}${date && time ? "T" : ""}${time ? time : ""}`;
        } else {
            const start = dateTime.start;
            const end = dateTime.end;

            if (start) {
                const date = start.date ? format(parse(start.date, "y-M-d", new Date()), "yyyy-MM-dd") : undefined;
                const time = start.time;
                str = `${date ? date : ""}${date && time ? "T" : ""}${time ? time : ""}`;
            }
            if (start && end) {
                str += ` --> `;
            }
            if (end) {
                const date = end.date ? format(parse(end.date, "y-M-d", new Date()), "yyyy-MM-dd") : undefined;
                const time = end.time;
                str += `${date ? date : ""}${date && time ? "T" : ""}${time ? time : ""}`;
            }
        }
    } catch (e) {
        console.error(e);
    }

    return str;
}

export type DurationFormatToMSMultiplier = Record<DurationFormat, number>;

/**
 * Lookup table to convert a duration format to a multiplier that will convert it to milliseconds
 */
export const DURATION_FORMAT_TO_MS_MULTIPLIER: DurationFormatToMSMultiplier = {
    // smallest, just multiple by 1
    ms: 1,
    millisecond: 1,
    milliseconds: 1,

    s: 1000,
    second: 1000,
    seconds: 1000,

    m: 60000,
    minute: 60000,
    minutes: 60000,

    h: 3.6e+6,
    hour: 3.6e+6,
    hours: 3.6e+6,

    d: 8.64e+7,
    day: 8.64e+7,
    days: 8.64e+7,

    w: 6.048e+8,
    week: 6.048e+8,
    weeks: 6.048e+8,

    M: 2.628e+9,
    month: 2.628e+9,
    months: 2.628e+9,
    // Quarters are tricky, going to assume 90 days
    Q: 7.776e+9,
    quarter: 7.776e+9,
    quarters: 7.776e+9,
    // Largest
    y: 3.154e+10,
    years: 3.154e+10,
    year: 3.154e+10
    // Room to add on more such as decade or century
};

/**
 * Builds a speakable and readable list from a set of items.
 *
 * For example, ["one", "two", "three", "four"] will be transformed
 * to "one, two, three or four".
 *
 * @param {string[]} items
 * @param {ListisizeDelimiter} [delimiter]
 */
//eslint-disable-next-line
//@ts-ignore
 export function listisize(items: string[], preferredDelimiter: ListDelimiter = ListDelimiter.or): string {
    const delimiter = ListDelimiter[preferredDelimiter];

    if (!items || items.length === 0) {
        return "";
    } else if (items.length === 1) {
        return items[0];
    } else if (items.length >= 2) {
        const secondToLast = items.length - 2;
        return items.reduce((current, item, currentIndex) => {
            current += item;

            if (currentIndex < secondToLast) {
                current += ", ";
            } else if (currentIndex === secondToLast) {
                current += ` ${delimiter} `;
            }

            return current;
        }, "");
    }
}
